export type CommunicationPolicyRefreshQueue = {
  request: () => void
  dispose: () => void
}

type CommunicationPolicyRefreshQueueOptions = {
  authenticated: () => boolean
  blocked: () => boolean
  refresh: () => Promise<void>
  schedule?: (callback: () => void, delayMs: number) => unknown
  cancel?: (handle: unknown) => void
}

export function createCommunicationPolicyRefreshQueue(
  options: CommunicationPolicyRefreshQueueOptions,
): CommunicationPolicyRefreshQueue {
  const schedule = options.schedule ?? ((callback, delayMs) => window.setTimeout(callback, delayMs))
  const cancel = options.cancel ?? (handle => window.clearTimeout(handle as number))
  let timer: unknown
  let pending = false
  let inFlight = false
  let disposed = false

  function request(): void {
    if (disposed) return
    pending = true
    scheduleFlush(0)
  }

  function scheduleFlush(delayMs: number): void {
    if (disposed || inFlight || timer !== undefined) return
    timer = schedule(() => {
      timer = undefined
      void flush()
    }, delayMs)
  }

  async function flush(): Promise<void> {
    if (disposed || !pending) return
    if (!options.authenticated()) {
      pending = false
      return
    }
    if (options.blocked()) {
      scheduleFlush(250)
      return
    }
    pending = false
    inFlight = true
    try {
      await options.refresh()
    } catch {
      // The normal session poll remains the fallback after a transient
      // invalidation refresh failure.
    } finally {
      inFlight = false
      if (pending) scheduleFlush(0)
    }
  }

  function dispose(): void {
    disposed = true
    pending = false
    if (timer !== undefined) {
      cancel(timer)
      timer = undefined
    }
  }

  return { request, dispose }
}
